/**
 * Realtime Service
 *
 * Handles real-time data synchronization via Supabase channels.
 * Extracted from supabase.ts RealtimeService class.
 *
 * @see PHASE4B_ARCHITECTURE.md for service specifications
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { AuthStoreAdapter, LearningStoreAdapter } from '@/services/adapters/store.adapters';

export class RealtimeService {
  private progressSubscription: any = null;
  private srsSubscriptions: any[] = [];
  private spanishVocabularyInitialized = false;

  constructor(
    private supabase: SupabaseClient,
    private authStore: AuthStoreAdapter,
    private learningStore: LearningStoreAdapter
  ) {}

  /**
   * Initialize real-time subscriptions for user progress
   */
  async initializeRealtime() {
    const user = this.authStore.getUser();
    if (!user) return;

    // Subscribe to user progress changes
    this.progressSubscription = this.supabase
      .channel('user_progress')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_progress',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          this.handleProgressUpdate(payload);
        }
      )
      .subscribe();

    // Initialize Spanish vocabulary real-time subscriptions
    this.initializeSpanishVocabularyRealtime(user.id);

    // Note: SRS-specific subscriptions are handled by the SRS store
    // to avoid conflicts and ensure proper cleanup
  }

  /**
   * Initialize Spanish vocabulary real-time subscriptions
   */
  async initializeSpanishVocabularyRealtime(userId: string) {
    if (this.spanishVocabularyInitialized) {
      return;
    }

    try {
      // Dynamic import to avoid circular dependencies
      const { spanishVocabularyRealtime } = await import('../realtime/spanish-vocabulary-realtime');

      spanishVocabularyRealtime.initializeUserSubscriptions(userId);

      this.spanishVocabularyInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize Spanish vocabulary real-time:', error);
    }
  }

  /**
   * Clean up all subscriptions
   */
  cleanup() {
    if (this.progressSubscription) {
      this.supabase.removeChannel(this.progressSubscription);
      this.progressSubscription = null;
    }

    // Clean up Spanish vocabulary real-time subscriptions
    this.cleanupSpanishVocabularyRealtime();

    // Clean up any lingering SRS subscriptions
    this.srsSubscriptions.forEach((subscription) => {
      try {
        this.supabase.removeChannel(subscription);
      } catch (error) {
        console.warn('Error cleaning up SRS subscription:', error);
      }
    });
    this.srsSubscriptions = [];
  }

  /**
   * Clean up Spanish vocabulary subscriptions
   */
  async cleanupSpanishVocabularyRealtime() {
    if (!this.spanishVocabularyInitialized) {
      return;
    }

    try {
      // Dynamic import to avoid circular dependencies
      const { spanishVocabularyRealtime } = await import('../realtime/spanish-vocabulary-realtime');

      spanishVocabularyRealtime.cleanupAllSubscriptions();

      this.spanishVocabularyInitialized = false;
    } catch (error) {
      console.error('❌ Error cleaning up Spanish vocabulary real-time:', error);
    }
  }

  /**
   * Handle progress update events from real-time channel
   */
  private handleProgressUpdate(payload: any) {
    try {
      if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
        const progressData = payload.new;
        this.learningStore.setProgress({
          totalWordsKnown: progressData.total_words_known,
          totalConversations: progressData.total_conversations,
          totalLessonsCompleted: progressData.total_lessons_completed,
          currentStreak: progressData.current_streak,
          longestStreak: progressData.longest_streak,
          weeklyGoal: progressData.weekly_goal,
          weeklyProgress: progressData.weekly_progress,
          cefr_level: progressData.cefr_level,
        });
        this.learningStore.setSyncStatus('idle');
      }
    } catch (error) {
      console.error('Error handling progress update:', error);
      this.learningStore.setSyncStatus('error');
    }
  }
}
